import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { select, Store } from "@ngrx/store";
import { Observable, firstValueFrom, takeUntil } from "rxjs";
import { BaseComponent } from "src/app/base.component";
import { Constants } from "src/app/shared/data/constants";
import {
  LocationAvailabilityEnum,
  OPERATIONAL_LOCATION_AVAILABILITIES,
} from "src/app/shared/enum/location-availability";
import {
  DatesAndLocationsAvailabilities,
  AdvertisingCampaignInterface,
  LocationWithAvailabilities,
} from "src/app/shared/models/advertising-campaigns/advertising-campaign.interface";
import { CalculatePrice } from "src/app/shared/models/calcul/calculatePrice";
import { TotalPrice } from "src/app/shared/models/calcul/totalPrice";
import { DateFilter } from "src/app/shared/models/date-filte.interface";
import { LocationPartnerInterface } from "src/app/shared/models/location-partners/locationPartner.interface";
import { BadgeService } from "src/app/shared/services/badge.service";
import { NewCampaignCalculatorService } from "src/app/shared/services/calculations/new-campaign.service";
import { LocationPartnersService } from "src/app/shared/services/location-partners.service";
import { AppStateInterface } from "src/app/store/appState.interface";
import { invokeLocationsToCreateCampaign } from "src/app/store/locationPartner/actions/get-locations.actions";
import {
  locationPartnersToCreateCampaignResultSelector,
} from "src/app/store/locationPartner/location-partner.selectors";
import { UtilFunctions } from "src/app/utils/utilFunctions";
import { DisplayAvailabilitiesComponent } from "../display-availabilities/display-availabilities.component";
import { UpdateBookingDatesComponent } from "../update-booking-dates/update-booking-dates.component";

@Component({
  selector: "app-select-locations-with-availabilities",
  templateUrl: "./select-locations-with-availabilities.component.html",
  styleUrls: ["./select-locations-with-availabilities.component.scss"],
})
export class SelectLocationsWithAvailabilitiesComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  @Input() locationsWithAvailabilities: LocationWithAvailabilities[] = [];
  @Input() endDate: Date;
  @Input() startDate: Date;
  @Input() ad: AdvertisingCampaignInterface;
  @Input() totalPricePerDay: TotalPrice[] = [];
  @Input("getLocationWithAvailabilities$")
  getLocationWithAvailabilities$: Observable<DatesAndLocationsAvailabilities>;

  @Output("emitLocationWithAvailabilities")
  emitLocationWithAvailabilities =
    new EventEmitter<LocationWithAvailabilities>();
  @Output("updatedLocationWithAvailability")
  updatedLocationWithAvailability = new EventEmitter<
    LocationWithAvailabilities[]
  >();

  locations: string[] = [];
  selectedLocations: string[] = [];
  form: FormGroup;

  locationPartnersResult$: Observable<LocationPartnerInterface[] | null>;

  availabilityStatusMap: Record<string, LocationAvailabilityEnum> = {};
  unavailableDatesMap: Record<string, DateFilter[]> = {};
  locationDatesMap: Record<string, DateFilter[]> = {};

  private availabilityComputationId = 0;

  constructor(
    private store: Store<AppStateInterface>,
    public badgeService: BadgeService,
    private newCampaignCalculatorService: NewCampaignCalculatorService,
    private fb: FormBuilder,
    private modalService: NgbModal,
    private locationPartnerService: LocationPartnersService
  ) {
    super(store);
    this.locationPartnersResult$ = this.store
      .pipe(select(locationPartnersToCreateCampaignResultSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.form = this.fb.group({
      locations: [[], [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.store.dispatch(invokeLocationsToCreateCampaign());
    this.initializeSelectedLocations();
    this.subscribeToLocationPartners();
    this.subscribeToAvailabilityUpdates();
    void this.refreshAvailabilityStatuses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["ad"] && this.ad && !this.selectedLocations.length) {
      this.initializeSelectedLocations();
    }

    if (
      changes["locationsWithAvailabilities"] ||
      changes["startDate"] ||
      changes["endDate"] ||
      changes["totalPricePerDay"] ||
      changes["ad"]
    ) {
      this.locationDatesMap = this.buildLocationDatesMap(
        this.locationsWithAvailabilities
      );
      void this.refreshAvailabilityStatuses();
    }
  }

  onSelectedLocationsChange(selectedLocations: string[] | string | null): void {
    this.selectedLocations = this.normalizeSelectedLocations(selectedLocations);
    this.patchSelectedLocations();
    this.locationsWithAvailabilities = this.filterLocationsBySelection(
      this.selectedLocations
    );
    this.updatedLocationWithAvailability.emit(this.locationsWithAvailabilities);
    this.onChangeCalculateCost();
  }

  onChangeCalculateCost(): void {
    this.newCampaignCalculatorService.onSelectCalculatePrice(
      this.startDate,
      this.endDate,
      this.ad?.objectiveName,
      this.ad?.calculationMethod,
      this.selectedLocations
    );
  }

  getAvailabilityStatusFromLocationPartnerAvailability(
    companyName: string
  ): LocationAvailabilityEnum {
    return (
      this.availabilityStatusMap[companyName] ??
      UtilFunctions.isAvailable(
        this.getLocationAvailabilityDates(companyName),
        this.startDate,
        this.endDate
      )
    );
  }

  getLocationAvailabilityDates(companyName: string): DateFilter[] {
    return this.locationDatesMap[companyName] ?? [];
  }

  shouldShowAvailabilityTooltip(companyName: string): boolean {
    return (
      this.getAvailabilityStatusFromLocationPartnerAvailability(companyName) !==
      LocationAvailabilityEnum.NOT_AVAILABLE
    );
  }

  canShowAllAvailablePeriods(companyName: string): boolean {
    return this.getLocationAvailabilityDates(companyName).length > 2;
  }

  canEditAvailability(companyName: string): boolean {
    const status =
      this.getAvailabilityStatusFromLocationPartnerAvailability(companyName);
    return (
      status !== LocationAvailabilityEnum.NOT_AVAILABLE &&
      !this.isOperationalAvailability(status)
    );
  }

  isOperationalAvailability(status: LocationAvailabilityEnum): boolean {
    return OPERATIONAL_LOCATION_AVAILABILITIES.includes(status);
  }

  removeLocationPartner(itemToRemove: string, event?: Event): void {
    event?.stopPropagation();
    this.onSelectedLocationsChange(
      this.selectedLocations.filter((item) => item !== itemToRemove)
    );
  }

  showModalUpdateAvailability(companyName: string, event?: Event): void {
    event?.stopPropagation();

    const modal = this.modalService.open(UpdateBookingDatesComponent, {
      centered: true,
    });

    modal.componentInstance.availableDates =
      this.getLocationAvailabilityDates(companyName);
    modal.componentInstance.companyName = companyName;
    modal.componentInstance.availabilityStatus =
      this.getAvailabilityStatusFromLocationPartnerAvailability(companyName);
    modal.componentInstance.advertisingCampaign = this.ad;
    modal.componentInstance.unavailableDates =
      this.unavailableDatesMap[companyName] ?? [];
    modal.componentInstance.startDate = this.startDate;
    modal.componentInstance.endDate = this.endDate;
    modal.componentInstance.selectLocationsWithAvailabilitiesModal = modal;

    modal.componentInstance.closeTheModal.subscribe(() => modal.close());
    modal.componentInstance.newAvailableDatesForLocationPartner.subscribe(
      (value: DateFilter[]) => {
        modal.close();
        this.emitLocationWithAvailabilities.emit({
          companyName,
          listOfAvailableDates: value,
        });
      }
    );
    modal.componentInstance.confirmDeletion.subscribe((value: boolean) => {
      if (value) {
        this.removeLocationPartner(companyName);
      }
    });
  }

  showAllAvailablePeriods(companyName: string, event?: Event): void {
    event?.stopPropagation();

    const modal = this.modalService.open(DisplayAvailabilitiesComponent, {
      centered: true,
    });
    modal.componentInstance.companyName = companyName;
    modal.componentInstance.dates =
      this.getLocationAvailabilityDates(companyName);
    modal.componentInstance.closeTheModal.subscribe(() => modal.close());
  }

  private subscribeToLocationPartners(): void {
    this.locationPartnersResult$.subscribe(
      (result: LocationPartnerInterface[] | null) => {
        if (!result) {
          return;
        }

        this.locations = result.map((value) => value.companyName);
        this.patchSelectedLocations();
      }
    );
  }

  private subscribeToAvailabilityUpdates(): void {
    if (!this.getLocationWithAvailabilities$) {
      return;
    }

    this.getLocationWithAvailabilities$
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe((locations) => {
        this.locationsWithAvailabilities = locations.locationWithAvailabilities;
        this.startDate = locations.startDate;
        this.endDate = locations.endDate;
        this.locationDatesMap = this.buildLocationDatesMap(
          this.locationsWithAvailabilities
        );
        void this.refreshAvailabilityStatuses();
      });
  }

  private initializeSelectedLocations(): void {
    this.selectedLocations = [...(this.ad?.locationPartners ?? [])];
    this.patchSelectedLocations();
  }

  private patchSelectedLocations(): void {
    this.form.patchValue(
      { locations: this.selectedLocations },
      { emitEvent: false }
    );
  }

  private normalizeSelectedLocations(
    selectedLocations: string[] | string | null | undefined
  ): string[] {
    if (!selectedLocations) {
      return [];
    }

    return Array.isArray(selectedLocations)
      ? [...selectedLocations]
      : [selectedLocations];
  }

  private filterLocationsBySelection(
    selectedLocations: string[]
  ): LocationWithAvailabilities[] {
    return (this.locationsWithAvailabilities ?? []).filter((location) =>
      selectedLocations.includes(location.companyName)
    );
  }

  private buildLocationDatesMap(
    locationAvailabilities: LocationWithAvailabilities[]
  ): Record<string, DateFilter[]> {
    return (locationAvailabilities ?? []).reduce<Record<string, DateFilter[]>>(
      (accumulator, location) => {
        accumulator[location.companyName] = this.getAvailabilityDates(
          location.companyName,
          locationAvailabilities
        );
        return accumulator;
      },
      {}
    );
  }

  private async refreshAvailabilityStatuses(): Promise<void> {
    const requestId = ++this.availabilityComputationId;
    const locations = this.locationsWithAvailabilities ?? [];

    if (!locations.length || !this.startDate || !this.endDate) {
      if (requestId === this.availabilityComputationId) {
        this.availabilityStatusMap = {};
        this.unavailableDatesMap = {};
      }
      return;
    }

    const nextStatusMap: Record<string, LocationAvailabilityEnum> = {};
    const nextUnavailableDatesMap: Record<string, DateFilter[]> = {};

    await Promise.all(
      locations.map(async ({ companyName }) => {
        const dates = this.locationDatesMap[companyName] ?? [];
        const calculatedAvailabilityStatus =
          this.getCalculatedAvailabilityStatus(companyName);

        if (
          calculatedAvailabilityStatus &&
          this.isOperationalAvailability(calculatedAvailabilityStatus)
        ) {
          nextStatusMap[companyName] = calculatedAvailabilityStatus;
          nextUnavailableDatesMap[companyName] = [];
          return;
        }

        const initialStatus = UtilFunctions.isAvailable(
          dates,
          this.startDate,
          this.endDate
        );
        nextStatusMap[companyName] = initialStatus;

        const resolvedAvailability = await this.resolveAvailabilityStatus(
          companyName,
          dates,
          initialStatus
        );

        nextStatusMap[companyName] = resolvedAvailability.status;
        nextUnavailableDatesMap[companyName] =
          resolvedAvailability.unavailableDates;
      })
    );

    if (requestId !== this.availabilityComputationId) {
      return;
    }

    this.availabilityStatusMap = nextStatusMap;
    this.unavailableDatesMap = nextUnavailableDatesMap;
  }

  private getCalculatedAvailabilityStatus(
    companyName: string
  ): LocationAvailabilityEnum | null {
    const calculatedLocation = (this.totalPricePerDay ?? []).find(
      (location) =>
        location.companyName === companyName
    );

    return calculatedLocation?.availabilities.availabilityStatus ?? null;
  }

  private async resolveAvailabilityStatus(
    companyName: string,
    availableDates: DateFilter[],
    initialStatus: LocationAvailabilityEnum
  ): Promise<{
    status: LocationAvailabilityEnum;
    unavailableDates: DateFilter[];
  }> {
    switch (initialStatus) {
      case LocationAvailabilityEnum.PARTIALLY: {
        const unavailableDates = await this.loadUnavailableRangesForGaps(
          companyName,
          availableDates
        );

        return {
          status: unavailableDates.length
            ? LocationAvailabilityEnum.PARTIALLY
            : LocationAvailabilityEnum.PARTIALLY_BOOKED,
          unavailableDates,
        };
      }

      case LocationAvailabilityEnum.NOT_AVAILABLE:
        return this.resolveUnavailableLocationStatus(companyName);

      default:
        return {
          status: initialStatus,
          unavailableDates: [],
        };
    }
  }

  private async resolveUnavailableLocationStatus(companyName: string): Promise<{
    status: LocationAvailabilityEnum;
    unavailableDates: DateFilter[];
  }> {
    const unavailableDates = await this.fetchUnavailableDateRanges(
      companyName,
      {
        startDate: this.startDate,
        endDate: this.endDate,
      }
    );

    if (unavailableDates.length === 0) {
      return {
        status: LocationAvailabilityEnum.PARTIALLY_BOOKED,
        unavailableDates: [],
      };
    }

    const unavailableCoverage = UtilFunctions.isAvailable(
      unavailableDates,
      this.startDate,
      this.endDate
    );

    return {
      status:
        unavailableCoverage === LocationAvailabilityEnum.AVAILABLE
          ? LocationAvailabilityEnum.NOT_AVAILABLE
          : LocationAvailabilityEnum.PARTIALLY,
      unavailableDates,
    };
  }

  getAvailabilityDates(
    companyName: string,
    locationAvailabilities: LocationWithAvailabilities[]
  ): DateFilter[] {
    const locationFromCurrentSelection = (locationAvailabilities ?? []).find(
      (location) => location.companyName === companyName
    );

    const fallbackLocation = this.ad?.locationsWithAvailabilities?.find(
      (location) => location.companyName === companyName
    );

    return this.sortDates(
      locationFromCurrentSelection?.listOfAvailableDates ??
        fallbackLocation?.listOfAvailableDates ??
        []
    );
  }

  private async fetchUnavailableDateRanges(
    companyName: string,
    range: DateFilter
  ): Promise<DateFilter[]> {
    const calculatePrice: CalculatePrice = {
      calculationMethod: this.ad.calculationMethod,
      startDate: range.startDate,
      endDate: range.endDate,
      objectiveName: this.ad.objectiveName,
      names: [companyName],
      checkDates: false,
    };

    const result = await firstValueFrom(
      this.locationPartnerService.calculateTotalLPPriceByCampanyNames(
        calculatePrice
      )
    );
    const totalPrice = result.totalPricePerDay?.[0];

    if (!totalPrice) {
      return [];
    }

    switch (totalPrice.availabilities.availabilityStatus) {
      case LocationAvailabilityEnum.AVAILABLE:
        return [];

      case LocationAvailabilityEnum.NOT_AVAILABLE:
      case LocationAvailabilityEnum.TEMPORARILY_SUSPENDED:
      case LocationAvailabilityEnum.MAINTENANCE:
        return [{ startDate: range.startDate, endDate: range.endDate }];

      case LocationAvailabilityEnum.PARTIALLY:
        return this.getUnavailableIntervals(
          range,
          totalPrice.availabilities.listOfUnpaidBookingIntervals
        );

      default:
        return [];
    }
  }

  private findMissingAvailabilityRanges(
    availableRanges: DateFilter[]
  ): DateFilter[] {
    const unavailableRanges: DateFilter[] = [];
    const campaignStart = new Date(this.startDate);
    const campaignEnd = new Date(this.endDate);
    const sortedRanges = this.sortDates(availableRanges);
    let current = new Date(campaignStart);

    for (const range of sortedRanges) {
      if (range.startDate && range.startDate > current) {
        unavailableRanges.push({
          startDate: new Date(current),
          endDate: new Date(range.startDate.getTime() - Constants.oneDay),
        });
      }

      if (range.endDate) {
        current = new Date(
          Math.max(
            current.getTime(),
            range.endDate.getTime() + Constants.oneDay
          )
        );
      }
    }

    if (current <= campaignEnd) {
      unavailableRanges.push({
        startDate: new Date(current),
        endDate: new Date(campaignEnd),
      });
    }

    return unavailableRanges;
  }

  private async loadUnavailableRangesForGaps(
    companyName: string,
    availableDates: DateFilter[]
  ): Promise<DateFilter[]> {
    const missingRanges = this.findMissingAvailabilityRanges(availableDates);

    if (!missingRanges.length) {
      return [];
    }

    const unavailableRanges: DateFilter[] = [];

    for (const range of missingRanges) {
      const existsInOriginalAvailability = this.getAvailabilityDates(
        companyName,
        this.ad?.locationsWithAvailabilities ?? []
      ).some((date) => this.isRangeIncluded(range, date));

      if (existsInOriginalAvailability) {
        continue;
      }

      unavailableRanges.push(
        ...(await this.fetchUnavailableDateRanges(companyName, range))
      );
    }

    return unavailableRanges;
  }

  private isRangeIncluded(range: DateFilter, container: DateFilter): boolean {
    if (
      !range.startDate ||
      !range.endDate ||
      !container.startDate ||
      !container.endDate
    ) {
      return false;
    }

    return (
      range.startDate.getTime() >= container.startDate.getTime() &&
      range.endDate.getTime() <= container.endDate.getTime()
    );
  }

  private sortDates(dates: DateFilter[]): DateFilter[] {
    return (dates ?? [])
      .filter(
        (range): range is { startDate: Date; endDate: Date } =>
          !!range.startDate &&
          !!range.endDate &&
          !isNaN(new Date(range.startDate).getTime()) &&
          !isNaN(new Date(range.endDate).getTime())
      )
      .map((range) => ({
        startDate: new Date(range.startDate),
        endDate: new Date(range.endDate),
      }))
      .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  }

  private getUnavailableIntervals(
    range: DateFilter,
    availableIntervals: DateFilter[]
  ): DateFilter[] {
    if (!range.startDate || !range.endDate) {
      return [];
    }

    const sortedAvailableIntervals = [...availableIntervals].sort(
      (a, b) =>
        new Date(a.startDate!).getTime() - new Date(b.startDate!).getTime()
    );

    const result: DateFilter[] = [];
    let current = new Date(range.startDate);

    for (const availableInterval of sortedAvailableIntervals) {
      const intervalStart = new Date(availableInterval.startDate!);
      const intervalEnd = new Date(availableInterval.endDate!);

      if (current < intervalStart) {
        result.push({
          startDate: new Date(current),
          endDate: new Date(intervalStart.getTime() - Constants.oneDay),
        });
      }

      current = new Date(intervalEnd.getTime() + Constants.oneDay);
    }

    const rangeEnd = new Date(range.endDate);
    if (current <= rangeEnd) {
      result.push({
        startDate: new Date(current),
        endDate: rangeEnd,
      });
    }

    return result;
  }
}
